import React, { useEffect, useState } from 'react';
import {
  FilterPersonnel,
  SortPersonnel,
  useGetAllPersonnel,
} from '../../../../traits/api/workRegisters/useGetAllPersonnel';
import { useGetInspectorStatistics } from '../../../../traits/api/workRegisters/useGetInspectorStatistics';
import { InspectorStatisticsTable } from './components/InspectorStatisticsTable';
import { useControl } from '../../components/ControlLayout';
import { Loading } from '../../../../components/Loading';
import { Error } from '../../../../components/Error';
import { WorkRegisterTable } from './components/WorkRegisterTable';
import { Images } from '@ansvar-sakerhet/ui/src/assets/images';
import { useNavigate, useParams } from 'react-router-dom';

import { S } from './WorkRegister.styles';
import { WorkRegisterSortDropdown } from './components/WorkRegisterSort/WorkRegisterSortDropdown';
import { WorkRegisterFilterDropdown } from './components/WorkRegisterFilter/WorkRegisterFilterDropdown';
import { SearchInput } from '../../../../components/SearchInput';
import { useMarkPersondataVerified } from '../../../../traits/api/controls/useMarkPersondataVerified';
import { formatControlDateTime } from '../../utils/reportTextTemplates';
import { useAppConfigContext } from '../../../../config/hooks/useAppConfig';

export const WorkRegister: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useAppConfigContext();
  const [loading, setIsLoading] = useState(true);
  const control = useControl();
  const [sort, setSort] = useState<SortPersonnel>(
    (localStorage.getItem(
      'sort-' + control.data?.controlId
    ) as SortPersonnel) || 'firstName_ASC'
  );

  const [filter, setFilter] = useState<FilterPersonnel>(
    (localStorage.getItem(
      'filter-' + control.data?.controlId
    ) as FilterPersonnel) || 'none'
  );

  const [page, setPage] = useState(
    Number(localStorage.getItem(control.data?.controlId))
  );
  const [showExtra, setShowExtra] = useState(false);
  const [search, setSearch] = useState('');

  const personnel = useGetAllPersonnel(
    control.data?.controlId,
    control.data?.workRegister?.workRegisterId,
    page,
    sort,
    filter,
    search
  );

  const inspectorStatistics = useGetInspectorStatistics(
    control.data?.controlId,
    control.data?.workRegister?.workRegisterId
  );

  const markVerified = useMarkPersondataVerified(control.data.inspectionId);

  useEffect(() => {
    if (personnel.isSuccess) {
      setIsLoading(false);
    }
  }, [personnel.data]);

  const { state } = useParams();
  React.useEffect(() => {
    const onScroll = () => {
      localStorage.setItem('Workregister', window.scrollY + '');
    };

    let scroll = Number(localStorage.getItem('Workregister'));
    if (!scroll) {
      scroll = 0;
    }
    new Promise((r) => setTimeout(r, 200)).then(() => {
      window.scrollTo({
        top: scroll,
        left: 0,
      });
    });

    window.removeEventListener('scroll', onScroll);
    window.addEventListener('scroll', onScroll, { passive: true });

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navigateToCoverPage = () => {
    window.scrollTo(0, 0);
    navigate(
      `/kontroller/${state}/${control.data.inspectionId}/underentreprenorer`
    );
  };

  const navigateToGenerateReportText = () => {
    window.scrollTo(0, 0);
    navigate(`/kontroller/${state}/${control.data.inspectionId}/resultat`, {
      state: { openGenerateReportText: true },
    });
  };

  if (personnel.isLoading || loading) {
    return <Loading />;
  }

  const isVerified = Boolean(control.data.personDataVerifiedAt);
  const isGenerateFeatureEnabled =
    config.ENABLE_PERSONDATA_REPORT_TEXT_GENERATION;

  return (
    <S.WorkRegisterContainer>
      {!control.data.gdprDataDeletedAt ? (
        <S.TableContainer>
          {personnel.isLoading && <Loading />}

          {personnel.isError && <Error refetch={personnel.refetch} />}

          {personnel.isSuccess && (
            <React.Fragment>
              <S.VerificationBar>
                <S.VerificationText>
                  {isVerified ? (
                    <>
                      Persondata verifierad av{' '}
                      <strong>{control.data.personDataVerifiedBy}</strong> den{' '}
                      {formatControlDateTime(control.data.personDataVerifiedAt)}
                    </>
                  ) : (
                    'Persondata är inte verifierad'
                  )}
                </S.VerificationText>
                <S.VerificationActions>
                  {!isVerified && (
                    <S.Button
                      $outlined
                      disabled={markVerified.isLoading}
                      onClick={() =>
                        markVerified.mutate({
                          controlId: control.data.controlId,
                        })
                      }
                    >
                      {markVerified.isLoading
                        ? 'Markerar...'
                        : 'Markera som verifierad'}
                    </S.Button>
                  )}
                  {isGenerateFeatureEnabled && (
                    <S.Button
                      $outlined
                      disabled={!isVerified}
                      title={
                        !isVerified
                          ? 'Markera Persondata som verifierad först'
                          : undefined
                      }
                      onClick={navigateToGenerateReportText}
                    >
                      Generera rapporttext
                    </S.Button>
                  )}
                </S.VerificationActions>
              </S.VerificationBar>

              <S.Margin $spacing="xs" />

              <S.B3 $color="darkGray">{personnel.count} st</S.B3>
              <S.HeaderActions>
                <SearchInput
                  input={search}
                  setInput={setSearch}
                  placeholder="Sök efter namn"
                />
                <WorkRegisterSortDropdown
                  sort={sort}
                  setSort={(sort: SortPersonnel) => {
                    localStorage.setItem(
                      'sort-' + control.data?.controlId,
                      sort
                    );
                    setSort(sort);
                  }}
                />
                <WorkRegisterFilterDropdown
                  filter={filter}
                  setFilter={(filter: FilterPersonnel) => {
                    localStorage.setItem(
                      'filter-' + control.data?.controlId,
                      filter
                    );
                    setFilter(filter);
                  }}
                />
                <S.Button
                  $outlined={showExtra}
                  onClick={() => {
                    setShowExtra(!showExtra);
                  }}
                >
                  {showExtra ? 'Dölj' : 'Visa'}
                </S.Button>
              </S.HeaderActions>

              <WorkRegisterTable
                data={personnel.data}
                loading={personnel.isLoading}
                page={page}
                setPage={(e: number) => {
                  setIsLoading(true);
                  personnel.remove();
                  localStorage.setItem(control.data?.controlId, e.toString());
                  setPage(e);
                }}
                showExtra={showExtra}
                numberOfPages={Math.ceil(personnel.count / 20)}
              />

              {inspectorStatistics.isSuccess && (
                <InspectorStatisticsTable
                  statistics={inspectorStatistics.data}
                />
              )}
            </React.Fragment>
          )}
        </S.TableContainer>
      ) : (
        <S.EmptyContainer>
          <img
            src={Images.Empty}
            width={279}
            height={204}
            alt="no content available"
          />

          <S.TextContainer>
            <S.H5>Här var det tomt!</S.H5>

            <S.B2>
              Det kan bero på att datan har raderats i ett tidigare skede
            </S.B2>
          </S.TextContainer>
        </S.EmptyContainer>
      )}

      <S.Footer>
        <S.Button $width={289} onClick={navigateToCoverPage}>
          Nästa
        </S.Button>
      </S.Footer>
    </S.WorkRegisterContainer>
  );
};
